/**
 * KAN-746: раздел "Заказы" интернет-магазина. Вкладки Корзины / Оформленные /
 * Подтверждённые / Отменённые со счётчиками, поиск, фильтр менеджера, фильтр
 * клиента (из карточки клиента: ?client=<id>), таблица и пагинация.
 * Вкладка и фильтры лежат в URL. Не интернет-магазин: API отвечает 404.
 */
import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { PaginationBar } from '@/components/common/PaginationBar';
import { PillTabs } from '@/components/dashboard/PillTabs';
import type { PillTab } from '@/components/dashboard/PillTabs';
import { ManagerFilter } from '@/components/clients/ManagerFilter';
import { ALL_MANAGERS } from '@/components/clients/useClientListFilters';
import { OrdersTable } from '@/components/orders/OrdersTable';
import { ORDER_TAB_LABELS } from '@/components/orders/orderFormat';
import { Input } from '@/components/ui/input';
import { isNotFoundError } from '@/hooks/useCatalog';
import { useOrderCounts, useOrdersList } from '@/hooks/useOrders';
import { useOrderFilters } from '@/hooks/useOrderFilters';
import { usePageSize } from '@/hooks/usePageSize';
import { clientsApi } from '@/services/api/clients';
import { clientQueryKey } from '@/services/api/clientQueries';
import { ORDER_TABS } from '@/types/order';
import type { OrderTab } from '@/types/order';

function ClientFilterChip({ clientId, onClear }: { clientId: string; onClear: () => void }) {
  const { data: client } = useQuery({
    queryKey: clientQueryKey(clientId),
    queryFn: () => clientsApi.getById(clientId),
    retry: false,
  });
  return (
    <span
      data-testid="orders-client-filter"
      className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-sm text-blue-800"
    >
      Клиент: {client?.name || client?.phone || '...'}
      <button type="button" aria-label="Показать заказы всех клиентов" onClick={onClear} className="rounded-full p-0.5 hover:bg-blue-100">
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

export function OrdersPage(): JSX.Element {
  const filters = useOrderFilters();
  const { tab, search, manager, client } = filters;
  const { page, setPage, pageSize, setPageSize } = usePageSize('orders');

  useEffect(() => {
    document.title = 'Заказы - SMETA AI';
  }, []);

  // Новый фильтр или вкладка: с первой страницы.
  useEffect(() => {
    setPage(1);
  }, [tab, search, manager, client, setPage]);

  const countsQuery = useOrderCounts();
  const listQuery = useOrdersList({
    tab,
    search: search || undefined,
    manager: manager === ALL_MANAGERS ? undefined : manager,
    client: client ?? undefined,
    page,
    page_size: pageSize,
  });

  const tabs = useMemo<PillTab<OrderTab>[]>(
    () =>
      ORDER_TABS.map((value) => {
        const count = countsQuery.data?.[value];
        return { value, label: count === undefined ? ORDER_TAB_LABELS[value] : `${ORDER_TAB_LABELS[value]} (${count})` };
      }),
    [countsQuery.data]
  );

  const orders = listQuery.data?.results ?? [];
  const count = listQuery.data?.count ?? 0;

  if (isNotFoundError(listQuery.error) || isNotFoundError(countsQuery.error)) {
    return (
      <PageLayout title="Заказы" breadcrumbs={[{ label: 'Заказы' }]} data-testid="orders-page">
        <div className="rounded-lg border bg-white p-6 text-sm text-gray-600">
          Заказы доступны только в режиме интернет-магазина.
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Заказы" breadcrumbs={[{ label: 'Заказы' }]} data-testid="orders-page">
      <div className="space-y-4">
        <PillTabs label="Заказы" tabs={tabs} value={tab} onChange={filters.setTab} testIdPrefix="orders-tab" />

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              aria-label="Поиск заказов"
              placeholder="Номер, клиент или товар"
              value={filters.searchInput}
              onChange={(e) => filters.setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <ManagerFilter value={manager} onChange={filters.setManager} />
        </div>

        {client && <ClientFilterChip clientId={client} onClear={filters.clearClient} />}

        <div className="rounded-lg border bg-white">
          {listQuery.isLoading ? (
            <p className="p-6 text-sm text-gray-500">Загрузка заказов...</p>
          ) : listQuery.isError ? (
            <p className="p-6 text-sm text-red-600">Не удалось загрузить заказы</p>
          ) : orders.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500" data-testid="orders-empty">
              <p>Заказов нет</p>
              {(search || manager !== ALL_MANAGERS || client) && (
                <p className="mt-1">Попробуйте изменить поиск или фильтры</p>
              )}
            </div>
          ) : (
            <OrdersTable orders={orders} />
          )}
        </div>

        <PaginationBar
          page={page}
          pageSize={pageSize}
          count={count}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          hasNext={listQuery.data ? !!listQuery.data.next : undefined}
          hasPrevious={listQuery.data ? !!listQuery.data.previous : undefined}
        />
      </div>
    </PageLayout>
  );
}

export default OrdersPage;
